import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { fileUrl } from "@/lib/api";
import { BrandLogo } from "@/components/BrandLogo";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { MessageCircle, X, Package, Loader2, ChevronLeft, ChevronRight, IndianRupee } from "lucide-react";

export default function PublicCatalogPage() {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true, data: null, error: null });
  const [gallery, setGallery] = useState(null); // { product, index }
  const [enquireProduct, setEnquireProduct] = useState(null);
  const API = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => { (async () => {
    try {
      const { data } = await axios.get(`${API}/api/public/c/${token}`);
      setState({ loading: false, data, error: null });
    } catch (e) {
      setState({ loading: false, data: null, error: e.response?.data?.detail || "Link expired or invalid" });
    }
  })(); }, [token, API]);

  const enquire = async (productId, message) => {
    try {
      await axios.post(`${API}/api/public/c/${token}/enquire`, { product_id: productId, message: message || null });
      toast.success("Thanks — we'll be in touch shortly.");
      setEnquireProduct(null);
      // Also open WhatsApp to the business if available
      if (state.data?.business_wa) {
        const prod = state.data.products.find(p => p.id === productId);
        const text = encodeURIComponent(`Hi, interested in ${prod?.design_code || prod?.title || 'a piece'} from your catalog.${message ? '\n\n' + message : ''}`);
        window.open(`https://wa.me/${state.data.business_wa}?text=${text}`, "_blank", "noopener,noreferrer");
      }
    } catch (e) {
      toast.error("Could not send. Try again.");
    }
  };

  if (state.loading) {
    return <div className="min-h-screen bg-[#FAF7EF] flex items-center justify-center text-[#4A5A50]" data-testid="catalog-loading"><Loader2 className="animate-spin mr-2" size={18}/>Loading your catalog…</div>;
  }
  if (state.error) {
    return <div className="min-h-screen bg-[#FAF7EF] flex items-center justify-center p-6">
      <div className="max-w-md text-center bg-white rounded-2xl border border-[#E4DFCF] p-8" data-testid="catalog-error">
        <div className="font-serif text-2xl">Link unavailable</div>
        <div className="text-[#4A5A50] mt-2 text-sm">{state.error}</div>
      </div>
    </div>;
  }
  const d = state.data;

  return (
    <div className="min-h-screen bg-[#FAF7EF] bg-grain" data-testid="public-catalog-page">
      {/* Header */}
      <header className="bg-[#083F22] text-white sticky top-0 z-30">
        <div className="max-w-5xl mx-auto px-4 lg:px-8 py-4 flex items-center justify-between">
          <BrandLogo size={36} showWordmark variant="dark"/>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#C9A24B]">Crafted at</div>
            <div className="text-sm font-medium" data-testid="catalog-company">{d.company_name}</div>
          </div>
        </div>
      </header>

      {/* Hero */}
      <div className="max-w-5xl mx-auto px-4 lg:px-8 pt-8 pb-4">
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#4A5A50]">Curated selection</div>
        <h1 className="font-serif text-4xl lg:text-5xl mt-1" data-testid="catalog-title">{d.title}</h1>
        {d.note && <p className="text-[#4A5A50] mt-3 leading-relaxed max-w-2xl" data-testid="catalog-note">{d.note}</p>}
        <div className="mt-4 text-xs text-[#4A5A50]">{d.products.length} pieces · tap any card to view full images</div>
      </div>

      {/* Product cards - weight-hero */}
      <div className="max-w-5xl mx-auto px-4 lg:px-8 pb-24">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {d.products.map(p => (
            <article key={p.id} className="bg-white rounded-2xl border border-[#E4DFCF] overflow-hidden card-hover" data-testid={`catalog-product-${p.design_code || p.id}`}>
              <button className="w-full aspect-[4/3] bg-[#F1EDDF] relative block" onClick={() => setGallery({ product: p, index: 0 })} data-testid={`catalog-open-gallery-${p.id}`}>
                {p.media?.[0] ? (
                  <img src={fileUrl(p.media[0].url || p.media[0].thumb_url)} alt={p.title} className="w-full h-full object-cover"/>
                ) : (
                  <div className="w-full h-full flex items-center justify-center"><Package size={40} className="text-[#C9A24B]"/></div>
                )}
                {p.labels?.length > 0 && (
                  <div className="absolute top-3 left-3 flex flex-wrap gap-1.5">
                    {p.labels.slice(0, 3).map(l => (
                      <span key={l} className="text-[10px] uppercase tracking-widest bg-black/60 backdrop-blur text-white px-2 py-1 rounded-full">{l}</span>
                    ))}
                  </div>
                )}
                {p.media?.length > 1 && (
                  <div className="absolute bottom-3 right-3 bg-black/50 text-white text-[10px] px-2 py-1 rounded-full">+{p.media.length - 1} more</div>
                )}
              </button>
              <div className="p-5">
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#0F5E39] font-semibold">{p.design_code || "—"}</div>
                <div className="font-serif text-2xl mt-1">{p.title}</div>
                <div className="text-xs text-[#4A5A50] mt-1">{p.sub_category || p.product_category_name || ""} · {p.metal} {p.purity || ""}</div>
                <div className="mt-4 flex items-end justify-between gap-3">
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-[#4A5A50]">Weight</div>
                    <div className="font-serif text-3xl font-medium text-[#6E5416]">{p.weight_grams ?? "—"} <span className="text-lg">g</span></div>
                  </div>
                  {p.is_price_visible && p.computed_price != null && (
                    <div className="text-right">
                      <div className="text-[10px] uppercase tracking-widest text-[#4A5A50]">Price</div>
                      <div className="font-serif text-2xl font-semibold text-[#0F5E39]"><IndianRupee size={14} className="inline mr-0.5"/>{Number(p.computed_price).toLocaleString('en-IN')}</div>
                    </div>
                  )}
                </div>
                {p.stone_details && <div className="mt-3 text-xs text-[#4A5A50]"><span className="text-[#6E5416]">Stones:</span> {p.stone_details}</div>}
                <button
                  onClick={() => setEnquireProduct(p)}
                  className="mt-4 w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full bg-[#25D366] hover:bg-[#1FAE55] text-white font-medium text-sm transition-colors"
                  data-testid={`catalog-enquire-${p.id}`}
                >
                  <MessageCircle size={14}/> Enquire on WhatsApp
                </button>
              </div>
            </article>
          ))}
        </div>
      </div>

      {/* Sticky footer CTA (mobile) */}
      {d.business_wa && (
        <div className="fixed bottom-0 left-0 right-0 md:hidden bg-white border-t border-[#E4DFCF] p-3 flex items-center justify-between gap-2 z-30 shadow-[0_-8px_24px_rgba(15,30,21,0.06)]">
          <div className="text-xs text-[#4A5A50]">Like something you see?</div>
          <a href={`https://wa.me/${d.business_wa}?text=${encodeURIComponent('Hi, interested in your catalog: ' + d.title)}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#25D366] text-white text-sm font-medium" data-testid="catalog-message-shop">
            <MessageCircle size={14}/> Message shop
          </a>
        </div>
      )}

      {/* Gallery lightbox */}
      {gallery && <GalleryLightbox gallery={gallery} setGallery={setGallery}/>}

      {/* Enquire dialog */}
      {enquireProduct && (
        <EnquireDialog product={enquireProduct} onCancel={() => setEnquireProduct(null)} onSubmit={(msg) => enquire(enquireProduct.id, msg)}/>
      )}

      <footer className="max-w-5xl mx-auto px-4 lg:px-8 py-8 text-center text-xs text-[#4A5A50]">
        Powered by <span className="font-serif text-sm text-[#0F5E39]">TARU</span> · We Shape Gold
      </footer>
    </div>
  );
}

function GalleryLightbox({ gallery, setGallery }) {
  const { product, index } = gallery;
  const media = product.media || [];
  const next = () => setGallery(g => ({ ...g, index: (g.index + 1) % media.length }));
  const prev = () => setGallery(g => ({ ...g, index: (g.index - 1 + media.length) % media.length }));
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
      if (e.key === "Escape") setGallery(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });
  const img = media[index];
  return (
    <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center" onClick={() => setGallery(null)} data-testid="catalog-gallery">
      <button className="absolute top-4 right-4 text-white/80 hover:text-white p-2" onClick={(e)=>{e.stopPropagation();setGallery(null);}} data-testid="gallery-close"><X size={22}/></button>
      <div className="absolute top-4 left-4 text-white/80 text-sm">
        <div className="text-[10px] uppercase tracking-widest text-[#C9A24B]">{product.design_code}</div>
        <div className="font-serif text-xl">{product.title}</div>
      </div>
      {media.length > 1 && <>
        <button className="absolute left-4 top-1/2 -translate-y-1/2 p-3 rounded-full bg-white/10 hover:bg-white/20 text-white" onClick={(e)=>{e.stopPropagation();prev();}} data-testid="gallery-prev"><ChevronLeft size={20}/></button>
        <button className="absolute right-4 top-1/2 -translate-y-1/2 p-3 rounded-full bg-white/10 hover:bg-white/20 text-white" onClick={(e)=>{e.stopPropagation();next();}} data-testid="gallery-next"><ChevronRight size={20}/></button>
      </>}
      <img src={fileUrl(img?.url || img?.thumb_url)} alt="" className="max-w-[90vw] max-h-[85vh] object-contain" onClick={(e)=>e.stopPropagation()}/>
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white/70 text-xs">{index + 1} / {media.length}</div>
    </div>
  );
}

function EnquireDialog({ product, onCancel, onSubmit }) {
  const [msg, setMsg] = useState("");
  return (
    <Dialog open={true} onOpenChange={onCancel}>
      <DialogContent data-testid="catalog-enquire-dialog">
        <div className="space-y-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#0F5E39] font-semibold">{product.design_code}</div>
            <div className="font-serif text-2xl">{product.title}</div>
            <div className="text-sm text-[#4A5A50] mt-1">{product.weight_grams ?? "—"} g · {product.metal} {product.purity || ""}</div>
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-[#4A5A50]">Anything specific? (optional)</label>
            <Textarea rows={3} value={msg} onChange={(e)=>setMsg(e.target.value)} placeholder="I'd like to know more about the weight tolerance and delivery timeline…" className="mt-1" data-testid="catalog-enquire-message"/>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onCancel} className="rounded-full">Cancel</Button>
            <Button onClick={() => onSubmit(msg)} className="flex-1 rounded-full bg-[#25D366] hover:bg-[#1FAE55] text-white" data-testid="catalog-enquire-submit">
              <MessageCircle size={14} className="mr-1.5"/> Send Enquiry
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
